import React, { useCallback, useEffect, useState } from 'react';
import { CardProductCodeName, CardProductTabName } from '../../constants/Card';
import { useLocation, useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Location } from 'history';
import { clickIncomeDeductionWeb } from 'common/appInterface';
import StringUtils from 'common/stringUtils';
import CardContainerContent from 'views/Main/CardContainerContent';
import { getVersionUrl } from 'common/cacheOverwrite';

type Props = {
  cardInfo: CardInfo;
  nonInoutTotalBalance: number;
  incomeDeductionAppVersion: number | null;
  clickBenefit: (value: CardInfo) => void;
  clickOldBenefit: (value: CardInfo) => void;
  clickCardUpgrade: (value: CardInfo) => void;
  clickMonthlyFeeHistory: (value: CardInfo) => void;
  clickFreeze: (value: CardInfo) => void;
  setReissueModal: React.Dispatch<React.SetStateAction<boolean>>;
  callMainNativeWeb: (mainYn: string) => void;
  moveToPendingFee: (value: CardInfo) => void;
  cardTerminateCancel: (value: CardInfo) => void;
  debitState: CardDebitState | null;
}

const CardContainer: React.FC<Props> = ({
                                          cardInfo,
                                          nonInoutTotalBalance,
                                          incomeDeductionAppVersion,
                                          clickBenefit,
                                          clickOldBenefit,
                                          clickCardUpgrade,
                                          clickMonthlyFeeHistory,
                                          clickFreeze,
                                          setReissueModal,
                                          callMainNativeWeb,
                                          moveToPendingFee,
                                          cardTerminateCancel,
                                          debitState
                                        }) => {
  /** Location State */
  const location = useLocation() as Location<LocationState>;
  /** 다국어처리 */
  const { t } = useTranslation();
  /** Navigate */
  const navigate = useNavigate();

  const { cardProductCode, cashYn, designDivisionCode, reissueReceiveYn, cardStatusCode } = cardInfo;
  const cashable: boolean = cashYn?.toUpperCase() === 'Y';

  const [cardStatus, setCardStatus] = useState<number>(5);
  const [content, setContent] = useState<JSX.Element>(<>
    <span>{t(`STAT01.benefit01.${cardProductCode}`)}</span>
    <span>{t(`STAT01.benefit02.${cardProductCode}`)}</span>
    <span>{t(`STAT01.benefit03.${cardProductCode}`)}</span>
  </>);

  const [isUpdateAvailable] = useState((designDivisionCode === "A"  && reissueReceiveYn !== "Y") && ((cardStatusCode === "11" || cardStatusCode === "22") && cardProductCode !== "101007"));

  const [button, setButton] = useState<JSX.Element>(<>
    <button></button>
  </>);

  const onClickHandle = () => {
    switch (cardStatus) {
      case 1: // Lost
        setReissueModal(true);
        break;
      case 2: // Freeze
        clickFreeze(cardInfo);
        break;
      case 3: // Pending Fee
        clickMonthlyFeeHistory(cardInfo);
        break;
      case 4: // Year-End Tax
        clickIncomeDeductionWeb();
        break;
      case 5: // Basic
        if (designDivisionCode === "A") {
          clickOldBenefit(cardInfo);
        } else {
          clickBenefit(cardInfo);
        }
        break;
      case 7:
        changePasswordClickHandle();
        break;
      case 8:
        cardTerminateCancel(cardInfo);
        break;
    }
  };

  const changePasswordClickHandle = useCallback(
    async () => {
      navigate('/manage/password/change',
        {
          state: {
            query: location.state.query,
            info: cardInfo
          }
        }
      );
    }, [location.state.query, cardInfo, navigate]);

  // 미입금 금액 클릭 시 화면 이동
  const clickPendingFee = useCallback(() => {
    moveToPendingFee(cardInfo);
  }, [cardInfo, moveToPendingFee]);

  const onCardImageClick = useCallback(() => {
    if (cardInfo.designDivisionCode === "A") {
      clickOldBenefit(cardInfo);
    } else {
      clickBenefit(cardInfo);
    }
  }, [cardInfo, clickBenefit, clickOldBenefit]);

  useEffect(() => {
    if (cardInfo.reqTerminateYn === 'Y') {
      // Terminate Progress
      setCardStatus(8);
    } else if ('22' === cardInfo.cardStatusCode) {
      // Lost
      setCardStatus(1);
    } else if ('21' === cardInfo.cardStatusCode) {
      // Freeze
      setCardStatus(2);
    } else if (nonInoutTotalBalance > 0) {
      // Pending Fee
      setCardStatus(3);
    } else if (location.state.query.incomeDeductionYn === 'Y'
      && (location.state.query.apiVersion != null && incomeDeductionAppVersion != null)
      && (parseInt(location.state.query.apiVersion) >= incomeDeductionAppVersion!)) {
      // Year-End Tax
      setCardStatus(4);
    } else if (cardInfo.passwordErrorsCntExceedYn !== undefined && cardInfo.passwordErrorsCntExceedYn === 'Y') {
      setCardStatus(7);
    } else {
      // Basic
      setCardStatus(5);
    }
  }, [cardInfo, nonInoutTotalBalance, location.state.query, incomeDeductionAppVersion]);

  useEffect(() => {
    let contentJSX;
    let buttonJSX;

    switch (cardStatus) {
      case 3:     // Pending Fee
        contentJSX = (<div className="content">
          <p className="title pendingFee">{t(`STAT01.header01.status_${cardStatus}`)}</p>
          <p className={`pendingAmount ${nonInoutTotalBalance > 0 ? 'underline' : ''}`}
             onClick={clickPendingFee}>
            {`${StringUtils.moneyFormat(StringUtils.nvlDefault(nonInoutTotalBalance, '0'))} KRW`}
          </p>
        </div>);
        break;
      case 1:     // Lost
      case 2:     // Freeze
        contentJSX = (<div className="content">
          <p className="title">{t(`STAT01.header01.status_${cardStatus}`)}</p>
          <p className="problem">{t(`STAT01.explan04.status_${cardStatus}`)}</p>
        </div>);
        break;
      case 4:     // Year-End Tax
        contentJSX = (<div className="content">
          <p className="title">{t(`STAT01.header01.status_${cardStatus}`)}</p>
          <p>{t(`STAT01.explan04.status_${cardStatus}`)}</p>
        </div>);
        break;
      case 7:
        contentJSX = (<div className="content">
          <p className={'title'}>{t('STAT07.title', 'Locked by Wrong Card Password Entered')}</p>
          <p>{t('STAT07.content', 'This card is locked due to entering wrong card password 5 times.')}</p>
        </div>);
        break;
      case 8:
        contentJSX = (<div className="content">
          <p className="title">{t('STAT08.title')}</p>
          <p className="problem">{t('STAT08.content')}</p>
        </div>);
        break;
      case 5:     // Basic
      default:    // Default(Welcome Card[103079]
        contentJSX = (<CardContainerContent cardInfo={cardInfo} hasButton={true} />);
        break;
    }

    switch (cardStatus) {
      case 1: // 분실 카드 A 디자인 업그레이드 표시
        if (isUpdateAvailable) {
          buttonJSX = (
            <button onClick={() => {clickCardUpgrade(cardInfo);}}
                    disabled={(reissueReceiveYn === 'Y' || cardInfo.useYn !== 'Y')}
            >
              {t(`STAT01.button.upgradeCard`, `Upgrade Card`)}
            </button>
          );
        } else {
          buttonJSX = (
            <button onClick={onClickHandle}
                    className={undefined}
                    disabled={cardInfo.cardStatusCode === '22' && (reissueReceiveYn === 'Y' || cardInfo.useYn !== 'Y')}
            >
              {t(`STAT01.button.status_${cardStatus}`)}
            </button>
          );
        }
        break;
      case 5:
        if (isUpdateAvailable) {
          buttonJSX = (
            <button onClick={() => {clickCardUpgrade(cardInfo);}}
                    disabled={cardInfo.cardStatusCode === '22' && (reissueReceiveYn === 'Y' || cardInfo.useYn !== 'Y')}
            >
              {t(`STAT01.button.upgradeCard`, `Upgrade Card`)}
            </button>
          );
        } else {
          buttonJSX = (
            <button onClick={onClickHandle}
                    disabled={cardInfo.cardStatusCode === '22' && (reissueReceiveYn === 'Y' || cardInfo.useYn !== 'Y')}
            >
              {t(`STAT01.button.status_${cardStatus}`)}
            </button>
          );
        }
        break;
      case 7:
        buttonJSX = (
          <button onClick={onClickHandle}>{t(`STAT07.button01`)}</button>
        );
        break;
      case 8:
        buttonJSX = (
          <button onClick={onClickHandle}>{t(`STAT08.button01`)}</button>
        );
        break;
      default:
        buttonJSX = (
          <button onClick={onClickHandle}
                  className={cardStatus === 3 ? 'pendingFeeBtn' : cardStatus === 4 ? 'yearEndTaxBtn' : undefined}
                  disabled={cardInfo.cardStatusCode === '22' && (reissueReceiveYn === 'Y' || cardInfo.useYn !== 'Y')}
          >
            {t(`STAT01.button.status_${cardStatus}`)}
          </button>
        );

        break;
    }

    setContent(contentJSX);
    setButton(buttonJSX);
    // eslint-disable-next-line
  }, [cardStatus, t, nonInoutTotalBalance, cardInfo, clickPendingFee]);


  return (
    <div className="cardWrap3">
      <div
        className={`img ${
          [1, 2, 7, 8].includes(cardStatus) ? 'card-disabled' : ''
        }`.trim()} onClick={onCardImageClick}>
        <img
          src={getVersionUrl(`${cardInfo.imgUrl}`)}
          alt={CardProductCodeName[Number(cardProductCode)]}
        />
        { isUpdateAvailable &&
          <div>
            <span style={{
              color: 'white',
              textDecoration: 'underline',
              fontWeight: 700,
              fontSize: '14px'
            }}>{t('STAT01.benefit')}</span>
          </div>
        }

        {[1, 2, 3, 4, 8].includes(cardStatus) && (<div>
          <button style={{
            color: 'white',
            textDecoration: 'underline',
            fontWeight: 700,
            fontSize: '14px'
          }}>{t('STAT01.benefit')}
          </button>
        </div>)}
      </div>
      <section>
        <div className={`cardTitle ${cashable ? 'atmTitle' : ''}`.trim()}>
          {`${CardProductTabName[Number(cardProductCode)]} ${
            cashable ? 'ATM+' : 'Card'
          }`}
        </div>
        {content}
        {button}
      </section>
    </div>
  );
};

export default CardContainer;
